import { Button, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Select, SelectItem, SelectSection, useDisclosure } from '@heroui/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useFormContext } from 'contexts/formContext'
import { ArrowDownWideNarrow, ListFilterPlus } from 'lucide-react'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useReadLocalStorage } from 'usehooks-ts'
import { $fetch, useFetch } from 'utils/fetch'
import { convertParamsToFilters } from 'utils/funcs/call-logs/filterFns'

export function SaveFilterModal() {
  const { isOpen, onOpen, onOpenChange } = useDisclosure()
  const [name, setName] = useState('')
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()

  const { mutate: saveFilter } = useMutation({
    mutationFn: () => $fetch('/v1/filters', {
      method: 'POST',
      body: { name, filters: convertParamsToFilters(searchParams) },
    }),
    onSuccess: ({ data: newFilter }) => {
      toast.success('Filters saved')
      onOpenChange(false)
      setName('')
      queryClient.setQueryData(['savedFilters'], old => [...(old || []), newFilter])
    },
    onError: () => {
      toast.error('Failed to save filters')
    },
  })

  return (
    <>
      <Button size="sm" variant="bordered" onPress={onOpen}>
        <ListFilterPlus size={16} />
        Save Filters
      </Button>
      <Modal
        radius="sm"
        placement="top-center"
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        classNames={{
          footer: 'ml-auto',
          body: 'flex flex-col gap-5 w-full',
          header: 'border-b border-gray-200 bg-[#18181b] w-full',
        }}
      >
        <ModalContent>
          {onClose => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                <h2 className={`
                  flex items-center gap-3 font-taurus text-2xl font-semibold
                  text-white
                `}
                >
                  Save Filters
                </h2>
                <p className={`
                  font-taurus text-xs font-light tracking-wider
                  text-foreground-400
                `}
                >
                  Saved filters are now shared with your organization.
                </p>
              </ModalHeader>
              <ModalBody>
                <Input
                  label="Filter name"
                  placeholder="Enter a name for your filter"
                  variant="bordered"
                  radius="sm"
                  value={name}
                  onValueChange={setName}
                />
              </ModalBody>
              <ModalFooter>
                <Button color="danger" variant="flat" onPress={onClose}>
                  Close
                </Button>
                <Button isDisabled={!name?.trim()} color="primary" onPress={saveFilter}>
                  Save
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  )
}

export function LoadFilterModal() {
  const form = useFormContext()
  const { isOpen, onOpen, onOpenChange } = useDisclosure()
  const [selectedParams, setSelectedParams] = useState(null)
  const legacyFilters = useReadLocalStorage('savedParams', [])

  const { data: savedFilters, isLoading: isLoadingFilters } = useFetch('/v1/filters/list', { queryKey: ['savedFilters'] })

  const handleLoadParams = () => {
    let filters
    try {
      // Load legacy local storage filters
      if (selectedParams.startsWith('filter')) {
        const searchParamsObj = new URLSearchParams(selectedParams)
        filters = convertParamsToFilters(searchParamsObj)
      }
      // Load organization-shared filters
      else {
        filters = savedFilters.find(filter => filter.id === selectedParams)?.filters
      }
      form.reset({ filters }, true)
      form.handleSubmit()
      toast.success('Filter set loaded')
      onOpenChange(false)
    }
    catch {
      toast.error('Failed to load filter set')
    }
  }

  const handleSelectionChange = (e) => {
    setSelectedParams(e.target.value)
  }

  return (
    <>
      <Button size="sm" variant="bordered" onPress={onOpen}>
        <ArrowDownWideNarrow size={16} />
        Load Filters
      </Button>
      <Modal
        radius="sm"
        placement="top-center"
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        classNames={{
          footer: 'ml-auto',
          body: 'flex flex-col gap-5 w-full',
          header: 'border-b border-gray-200 bg-[#18181b] w-full',
        }}
      >
        <ModalContent>
          {onClose => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                <h2 className={`
                  flex items-center gap-3 font-taurus text-2xl font-semibold
                  text-white
                `}
                >
                  Load Saved Filters
                </h2>
                <p className={`
                  font-taurus text-xs font-light tracking-wider
                  text-foreground-400
                `}
                >
                  Load organization-shared filters or your local legacy filters.
                </p>
              </ModalHeader>
              <ModalBody>
                <Select isLoading={isLoadingFilters} label="Select a saved set" selectedKeys={[selectedParams]} onChange={handleSelectionChange} radius="sm" variant="bordered">
                  {savedFilters?.length > 0 && (
                    <SelectSection showDivider title="Shared Filters">
                      {savedFilters?.map(savedFilter => (
                        <SelectItem key={savedFilter.id}>{savedFilter.name}</SelectItem>
                      ))}
                    </SelectSection>
                  )}
                  {Array.isArray(legacyFilters) && legacyFilters?.length > 0 && (
                    <SelectSection title="Local Filters">
                      {legacyFilters?.map(legacyFilter => (
                        <SelectItem key={legacyFilter.params}>{legacyFilter.name}</SelectItem>
                      ))}
                    </SelectSection>
                  )}
                </Select>
              </ModalBody>
              <ModalFooter>
                <Button color="danger" variant="flat" onPress={onClose}>
                  Close
                </Button>
                <Button
                  isDisabled={!selectedParams}
                  color="primary"
                  onPress={handleLoadParams}
                >
                  Load
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  )
}
